import type {
  AsyncStorage,
  PersistenceOptions,
  SyncStorage,
} from "@solid-primitives/storage";
import type { PickedSubcategory } from "@/domains/freelance/taxonomy/types";
import { isJobCategory } from "@/domains/job-offer/create/constants";
import { createDefaultSector, onboardingStepsLabels } from "../constants";
import type {
  OnboardingStep,
  OnboardingStore,
  SectorErrorCode,
} from "../context/OnboardingContext";

const stepIndex = (step: OnboardingStep) =>
  onboardingStepsLabels.findIndex((label) => label.step === step);

const SECTOR_STEP_INDEX = stepIndex("sector");

/**
 * An onboarding draft as it comes out of storage: written by this version, or
 * by the one before the `sector` step, which kept the category in its own
 * `job_category` section (freelancer-categories D5). Nothing in `sector` is
 * trusted until the sanitizer has checked it.
 */
export type PersistedOnboardingDraft = Omit<OnboardingStore, "sector"> & {
  sector?: {
    value?: {
      job_category?: unknown;
      job_category_secondary?: unknown;
      job_subcategories?: unknown;
    };
    source?: unknown;
    reviewed?: unknown;
    error?: unknown;
  };
  job_category?: { value?: unknown };
};

/**
 * True while the onboarding has none of the 8 categories (never chosen, or one
 * of the 4 removed): «Avanti» of `sector`, «Crea account» and «Torna alla
 * revisione» stay disabled (taxonomy-contract-pr3 D8, freelancer-categories
 * AC16).
 */
export const isOnboardingCategoryMissing = (
  store: Pick<OnboardingStore, "sector">,
): boolean => !isJobCategory(store.sector.value.job_category);

const isFilled = (value: unknown) =>
  value !== null && value !== undefined && value !== "";

const isPickedSubcategory = (value: unknown): value is PickedSubcategory =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { key?: unknown }).key === "string";

const isSectorErrorCode = (value: unknown): value is SectorErrorCode =>
  value === "required" || value === "sector_reset";

/**
 * The `sector` section of a persisted draft: migrated from the old
 * `job_category` section when the draft predates the step, with a default for
 * whatever is missing. A category outside the 8 (primary or secondary) is
 * emptied and leaves the `sector_reset` code for the step to explain (AC20);
 * a subcategory without a string key is dropped.
 */
const sanitizeSector = (
  draft: PersistedOnboardingDraft,
): OnboardingStore["sector"] => {
  const saved = draft.sector;
  if (!saved && !draft.job_category) {
    return createDefaultSector();
  }

  const savedPrimary = saved
    ? saved.value?.job_category
    : draft.job_category?.value;
  const savedSecondary = saved?.value?.job_category_secondary;
  const primary = isJobCategory(savedPrimary) ? savedPrimary : null;
  const secondary = isJobCategory(savedSecondary) ? savedSecondary : null;
  const emptied =
    (isFilled(savedPrimary) && primary === null) ||
    (isFilled(savedSecondary) && secondary === null);
  const subcategories = saved?.value?.job_subcategories;

  return {
    value: {
      job_category: primary,
      job_category_secondary: secondary,
      job_subcategories: Array.isArray(subcategories)
        ? subcategories.filter(isPickedSubcategory)
        : [],
    },
    source:
      saved?.source === "cv" || saved?.source === "linkedin"
        ? saved.source
        : null,
    // A migrated draft with a category had it from the old «Competenze» step:
    // the freelancer has seen it.
    reviewed: saved ? saved.reviewed === true : primary !== null,
    error: emptied
      ? "sector_reset"
      : isSectorErrorCode(saved?.error)
        ? saved.error
        : null,
  };
};

/**
 * Fixes a persisted onboarding draft (taxonomy-contract-pr3 D8,
 * freelancer-categories D5): the old `job_category` section moves into
 * `sector`, a category outside the 8 is emptied, and a draft past `sector`
 * without a primary category goes back to `sector`. Returns a new object;
 * everything else is left as it is.
 */
export const sanitizeOnboardingDraft = (
  draft: PersistedOnboardingDraft,
): OnboardingStore => {
  const { job_category: _, ...rest } = draft;
  const sector = sanitizeSector(draft);
  const pastSector = stepIndex(draft.step) > SECTOR_STEP_INDEX;

  return {
    ...rest,
    step:
      sector.value.job_category === null && pastSector ? "sector" : draft.step,
    sector,
  };
};

/**
 * `makePersisted` options of the onboarding draft. The draft is fixed only in
 * `deserialize`, never by writing to the store after load: localforage reads
 * asynchronously, and a write landing first would discard the saved draft
 * (taxonomy-contract-pr3 D7).
 */
export const onboardingStorePersistOptions = (
  storage: SyncStorage | AsyncStorage | undefined,
): PersistenceOptions<OnboardingStore, undefined> => ({
  name: "onboarding_store",
  storage,
  deserialize: (raw) => sanitizeOnboardingDraft(JSON.parse(raw)),
});
